/**
 * Professional Education Auto-Suggest API Service
 * Provides intelligent institution and location suggestions with caching
 */

// Types for education data
export interface University {
  name: string;
  country: string;
  country_code?: string;
  web_pages: string[];
  domains: string[];
  state_province?: string;
}

export interface Country {
  name: string;
  code: string;
  flag?: string;
}

export interface City {
  name: string;
  country: string;
  country_code?: string;
  population?: number;
  latitude?: number;
  longitude?: number;
}

export interface AutoSuggestResult {
  institutions: University[];
  cached: boolean;
  totalResults: number;
}

// Cache management
class CacheManager {
  private cache = new Map<string, { data: any; timestamp: number; ttl: number }>();
  
  set(key: string, data: any, ttlMinutes = 30) {
    this.cache.set(key, {
      data,
      timestamp: Date.now(),
      ttl: ttlMinutes * 60 * 1000
    });
  }
  
  get(key: string): any | null {
    const cached = this.cache.get(key);
    if (!cached) return null;
    
    if (Date.now() - cached.timestamp > cached.ttl) {
      this.cache.delete(key);
      return null;
    }
    
    return cached.data;
  }
  
  clear() {
    this.cache.clear();
  }
  
  size() {
    return this.cache.size;
  }
}

// Global cache instance
const cache = new CacheManager();

// API Configuration
const UNIVERSITY_API_BASE = 'http://universities.hipolabs.com';
const COUNTRIES_API = 'https://restcountries.com/v3.1/all?fields=name,cca2,flag';
const CITIES_API = 'https://api.api-ninjas.com/v1/city';
const CITIES_API_KEY = 'YOUR_API_KEY'; // Free API key from API Ninjas

// Common institutions fallback (for offline/error scenarios)
const COMMON_INSTITUTIONS: { [key: string]: string[] } = {
  'bangladesh': [
    'University of Dhaka', 'Bangladesh University of Engineering and Technology',
    'North South University', 'American International University-Bangladesh',
    'BRAC University', 'East West University', 'Independent University Bangladesh',
    'Daffodil International University', 'United International University',
    'Bangladesh Agricultural University', 'Chittagong University of Engineering & Technology'
  ],
  'pakistan': [
    'University of Karachi', 'University of Punjab', 'University of Engineering and Technology Lahore',
    'NED University of Engineering & Technology', 'Lahore University of Management Sciences',
    'National University of Sciences and Technology', 'Quaid-i-Azam University',
    'University of Agriculture Faisalabad', 'Sindh University', 'Peshawar University'
  ],
  'india': [
    'Indian Institute of Technology Delhi', 'Indian Institute of Technology Bombay',
    'Indian Institute of Management Ahmedabad', 'University of Delhi', 'Jawaharlal Nehru University',
    'Anna University', 'Bangalore University', 'Pune University', 'Calcutta University',
    'Indian Institute of Science', 'Aligarh Muslim University', 'Banaras Hindu University'
  ],
  'united kingdom': [
    'University of Oxford', 'University of Cambridge', 'Imperial College London',
    'London School of Economics', 'University College London', 'King\'s College London',
    'University of Edinburgh', 'University of Manchester', 'University of Warwick',
    'University of Bristol', 'University of Glasgow', 'Durham University'
  ],
  'united states': [
    'Harvard University', 'Massachusetts Institute of Technology', 'Stanford University',
    'California Institute of Technology', 'Princeton University', 'Yale University',
    'Columbia University', 'University of Chicago', 'University of Pennsylvania',
    'Johns Hopkins University', 'Northwestern University', 'Duke University'
  ]
};

// Common cities fallback (for offline/error scenarios)
const COMMON_CITIES: { [key: string]: string[] } = {
  'bangladesh': [
    'Dhaka', 'Chittagong', 'Sylhet', 'Rajshahi', 'Barisal', 'Rangpur', 
    'Comilla', 'Gazipur', 'Narayanganj', 'Mymensingh', 'Khulna'
  ],
  'pakistan': [
    'Karachi', 'Lahore', 'Islamabad', 'Faisalabad', 'Rawalpindi', 'Peshawar',
    'Multan', 'Gujranwala', 'Hyderabad', 'Quetta', 'Sialkot'
  ],
  'india': [
    'Mumbai', 'Delhi', 'Bangalore', 'Hyderabad', 'Chennai', 'Kolkata',
    'Pune', 'Ahmedabad', 'Jaipur', 'Lucknow', 'Kanpur', 'Nagpur'
  ],
  'united kingdom': [
    'London', 'Birmingham', 'Manchester', 'Leeds', 'Liverpool', 'Sheffield',
    'Bristol', 'Glasgow', 'Edinburgh', 'Cardiff', 'Belfast', 'Nottingham'
  ],
  'united states': [
    'New York', 'Los Angeles', 'Chicago', 'Houston', 'Phoenix', 'Philadelphia',
    'San Antonio', 'San Diego', 'Dallas', 'San Jose', 'Austin', 'Jacksonville'
  ],
  'canada': [
    'Toronto', 'Montreal', 'Vancouver', 'Calgary', 'Edmonton', 'Ottawa',
    'Winnipeg', 'Quebec City', 'Hamilton', 'Kitchener', 'London', 'Victoria'
  ],
  'australia': [
    'Sydney', 'Melbourne', 'Brisbane', 'Perth', 'Adelaide', 'Gold Coast',
    'Newcastle', 'Canberra', 'Sunshine Coast', 'Wollongong', 'Geelong', 'Hobart'
  ]
};

/**
 * Debounce function for API calls
 */
export function debounce<T extends (...args: any[]) => any>(
  func: T,
  wait: number
): (...args: Parameters<T>) => Promise<ReturnType<T>> {
  let timeout: NodeJS.Timeout;
  
  return (...args: Parameters<T>): Promise<ReturnType<T>> => {
    return new Promise((resolve) => {
      clearTimeout(timeout);
      timeout = setTimeout(() => resolve(func(...args)), wait);
    });
  };
}

/**
 * Search universities/institutions with intelligent caching
 */
export async function searchInstitutions(
  query: string,
  country?: string,
  limit = 10
): Promise<AutoSuggestResult> {
  // Input validation
  if (!query || query.trim().length < 2) {
    return { institutions: [], cached: false, totalResults: 0 };
  }
  
  const normalizedQuery = query.toLowerCase().trim();
  const cacheKey = `institutions_${normalizedQuery}_${country || 'all'}_${limit}`;
  
  // Check cache first
  const cachedResult = cache.get(cacheKey);
  if (cachedResult) {
    return { ...cachedResult, cached: true };
  }
  
  try {
    // Build API URL
    const params = new URLSearchParams();
    params.append('name', normalizedQuery);
    if (country) {
      params.append('country', country);
    }
    
    const url = `${UNIVERSITY_API_BASE}/search?${params}`;
    
    // Make API request with timeout
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 5000);
    
    const response = await fetch(url, {
      signal: controller.signal,
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json'
      }
    });
    
    clearTimeout(timeoutId);
    
    if (!response.ok) {
      throw new Error(`API request failed: ${response.status}`);
    }
    
    const institutions: University[] = await response.json();
    
    // Filter and enhance results
    const processedResults = institutions
      .filter(uni => uni.name && uni.name.toLowerCase().includes(normalizedQuery))
      .slice(0, limit)
      .map(uni => ({
        ...uni,
        name: uni.name.trim(),
        country: uni.country || 'Unknown'
      }));
    
    const result = {
      institutions: processedResults,
      cached: false,
      totalResults: processedResults.length
    };
    
    // Cache successful results
    cache.set(cacheKey, result, 60); // Cache for 1 hour
    
    return result;
    
  } catch (error) {
    console.warn('University API error, falling back to local data:', error);
    
    // Fallback to common institutions
    const fallbackResults = getFallbackInstitutions(normalizedQuery, country, limit);
    return {
      institutions: fallbackResults,
      cached: false,
      totalResults: fallbackResults.length
    };
  }
}

/**
 * Get fallback institutions from local data
 */
function getFallbackInstitutions(query: string, country?: string, limit = 10): University[] {
  const normalizedQuery = query.toLowerCase();
  let institutions: string[] = [];
  
  if (country) {
    const countryKey = country.toLowerCase();
    institutions = COMMON_INSTITUTIONS[countryKey] || [];
  } else {
    // Search all countries
    institutions = Object.values(COMMON_INSTITUTIONS).flat();
  }
  
  return institutions
    .filter(name => name.toLowerCase().includes(normalizedQuery))
    .slice(0, limit)
    .map(name => ({
      name,
      country: country || 'Various',
      web_pages: [],
      domains: []
    }));
}

/**
 * Get list of countries with caching
 */
export async function getCountries(): Promise<Country[]> {
  const cacheKey = 'countries_list';
  const cachedCountries = cache.get(cacheKey);
  
  if (cachedCountries) {
    return cachedCountries;
  }
  
  try {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 5000);
    
    const response = await fetch(COUNTRIES_API, {
      signal: controller.signal
    });
    
    clearTimeout(timeoutId);
    
    if (!response.ok) {
      throw new Error(`Countries API failed: ${response.status}`);
    }
    
    const data = await response.json();
    
    const countries: Country[] = data
      .map((country: any) => ({
        name: country.name.common,
        code: country.cca2,
        flag: country.flag
      }))
      .sort((a: Country, b: Country) => a.name.localeCompare(b.name));
    
    // Cache for 24 hours
    cache.set(cacheKey, countries, 24 * 60);
    
    return countries;
    
  } catch (error) {
    console.warn('Countries API error, using fallback:', error);
    
    // Fallback country list
    const fallbackCountries = [
      { name: 'Bangladesh', code: 'BD', flag: '🇧🇩' },
      { name: 'Pakistan', code: 'PK', flag: '🇵🇰' },
      { name: 'India', code: 'IN', flag: '🇮🇳' },
      { name: 'United Kingdom', code: 'GB', flag: '🇬🇧' },
      { name: 'United States', code: 'US', flag: '🇺🇸' },
      { name: 'Canada', code: 'CA', flag: '🇨🇦' },
      { name: 'Australia', code: 'AU', flag: '🇦🇺' },
      { name: 'Germany', code: 'DE', flag: '🇩🇪' },
      { name: 'France', code: 'FR', flag: '🇫🇷' },
      { name: 'Netherlands', code: 'NL', flag: '🇳🇱' }
    ];
    
    return fallbackCountries;
  }
}

/**
 * Search countries by name
 */
export async function searchCountries(query: string): Promise<Country[]> {
  if (!query || query.trim().length < 1) {
    return [];
  }

  const countries = await getCountries();
  const normalizedQuery = query.toLowerCase().trim();
  
  return countries
    .filter(country => 
      country.name.toLowerCase().includes(normalizedQuery) ||
      country.code.toLowerCase().includes(normalizedQuery)
    )
    .slice(0, 10);
}

/**
 * Get cities with optional country filtering
 */
export async function getCities(query: string, country?: string, limit = 10): Promise<City[]> {
  if (!query || query.trim().length < 2) {
    return [];
  }

  const normalizedQuery = query.toLowerCase().trim();
  const cacheKey = `cities_${normalizedQuery}_${country || 'all'}_${limit}`;
  
  // Check cache first
  const cachedResult = cache.get(cacheKey);
  if (cachedResult) {
    return cachedResult;
  }

  try {
    // For now, use fallback data until we get API key
    // In production, you would use the Cities API
    const cities = getFallbackCities(normalizedQuery, country, limit);
    
    // Cache successful results
    cache.set(cacheKey, cities, 30); // Cache for 30 minutes
    
    return cities;
    
  } catch (error) {
    console.warn('Cities API error, falling back to local data:', error);
    return getFallbackCities(normalizedQuery, country, limit);
  }
}

/**
 * Get fallback cities from local data
 */
function getFallbackCities(query: string, country?: string, limit = 10): City[] {
  const normalizedQuery = query.toLowerCase();
  let cities: string[] = [];
  
  if (country) {
    const countryKey = country.toLowerCase();
    cities = COMMON_CITIES[countryKey] || [];
  } else {
    // Search all countries
    cities = Object.entries(COMMON_CITIES).flatMap(([countryName, cityList]) => 
      cityList.map(city => `${city}|${countryName}`)
    );
  }
  
  const filteredCities = cities
    .filter(cityData => {
      const cityName = cityData.includes('|') ? cityData.split('|')[0] : cityData;
      return cityName.toLowerCase().includes(normalizedQuery);
    })
    .slice(0, limit);
  
  return filteredCities.map(cityData => {
    if (cityData.includes('|')) {
      const [cityName, countryName] = cityData.split('|');
      return {
        name: cityName,
        country: countryName.charAt(0).toUpperCase() + countryName.slice(1)
      };
    } else {
      return {
        name: cityData,
        country: country || 'Various'
      };
    }
  });
}

/**
 * Debounced search functions for components
 */
export const debouncedSearchInstitutions = debounce(searchInstitutions, 300);
export const debouncedSearchCountries = debounce(searchCountries, 200);
export const debouncedGetCities = debounce(getCities, 200);

/**
 * Utility functions
 */
export const educationUtils = {
  // Clear all caches
  clearCache: () => cache.clear(),
  
  // Get cache statistics
  getCacheStats: () => ({
    size: cache.size(),
    entries: cache.size()
  }),
  
  // Preload common data
  preloadCommonData: async () => {
    try {
      await Promise.all([
        getCountries(),
        searchInstitutions('university', 'bangladesh'),
        searchInstitutions('university', 'pakistan'),
        searchInstitutions('university', 'united kingdom')
      ]);
    } catch (error) {
      console.warn('Preload failed:', error);
    }
  }
};

export default {
  searchInstitutions: debouncedSearchInstitutions,
  searchCountries: debouncedSearchCountries,
  getCities: debouncedGetCities,
  getCountries,
  utils: educationUtils
};
